import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IUsuario, User } from '@core/user/user.types';
import { IEmpresa } from '@shared/models/empresa';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { Observable, of, switchMap, throwError, Subscription } from 'rxjs';
import { NavigationService } from '@core/navigation/navigation.service';
import { EmpresaService } from '@shared/service/app/empresa.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private user: IUsuario;
    private _navigationMenu: any[] = [];
    private rutas: string[] = [];
    private _empresa: IEmpresa;
    tiposArticuloPermitidos: string[] = [];
    tipoCliente: string[] = [];
    avisado = false;
    private subscription: Subscription;
    private _initialTokenResolve: (() => void) | null = null;
    private _initialTokenPromise: Promise<void> | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _navigationService: NavigationService,
        private _empresaService: EmpresaService,
    ) {
        this._initialTokenPromise = new Promise<void>((resolve) => {
            this._initialTokenResolve = resolve;
        });
        this._setupPostMessageListener();
    }

    private _isEmbedded(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    private _setupPostMessageListener(): void {
        if (typeof window !== 'undefined') {
            window.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'SET_TOKEN' && event.data.token) {
                    // Si viene la información del usuario en el mismo mensaje del token, la guardamos primero
                    if (event.data.user) {
                        const extUser = event.data.user;
                        const formattedUser = {
                            name: `${extUser.nombre || ''} ${extUser.apellido || ''}`.trim() || extUser.nick || 'Usuario Externo',
                            email: extUser.email || ''
                        };
                        this._userService.externalUser = formattedUser;
                    }

                    // Si el token es idéntico al actual, no recargamos la página
                    if (this.accessToken === event.data.token) {
                        if (!this._authenticated) {
                            this.applyExternalToken(event.data.token);
                        }

                        if (this._initialTokenResolve) {
                            this._initialTokenResolve();
                            this._initialTokenResolve = null;
                        }
                        return;
                    }

                    // Si el token es diferente, aplicamos el nuevo token y recargamos si corresponde
                    const success = this.applyExternalToken(event.data.token);
                    if (this._initialTokenResolve) {
                        this._initialTokenResolve();
                        this._initialTokenResolve = null;
                    } else if (success) {
                        window.location.reload();
                    }
                }

                if (event.data && event.data.type === 'SET_EXTERNAL_USER' && event.data.user) {
                    const extUser = event.data.user;
                    const formattedUser = {
                        name: `${extUser.nombre || ''} ${extUser.apellido || ''}`.trim() || extUser.nick || 'Usuario Externo',
                        email: extUser.email || '',
                        administrador_backlog: extUser.administrador_backlog ?? extUser.admin_backlog
                    };
                    this._userService.externalUser = formattedUser;
                }
            });

            if (this._isEmbedded()) {
                const urlParams = new URLSearchParams(window.location.search);
                const urlToken = urlParams.get('token');

                // Intentar leer el usuario externo desde los parámetros de la URL
                const nombre = urlParams.get('nombre') || urlParams.get('usuario_nombre');
                const apellido = urlParams.get('apellido') || urlParams.get('usuario_apellido');
                const nick = urlParams.get('nick') || urlParams.get('usuario_nick');
                const email = urlParams.get('email') || urlParams.get('usuario_email');
                const adminBacklogParam = urlParams.get('administrador_backlog') || urlParams.get('admin_backlog');

                if (nombre || apellido || nick || adminBacklogParam !== null) {
                    const formattedUser = {
                        name: `${nombre || ''} ${apellido || ''}`.trim() || nick || 'Usuario Externo',
                        email: email || '',
                        administrador_backlog: adminBacklogParam
                    };
                    this._userService.externalUser = formattedUser;
                }

                if (urlToken) {
                    this.applyExternalToken(urlToken);
                }

                // SIEMPRE enviamos IFRAME_READY para avisar que el listener de postMessage ya está activo y listo
                window.parent.postMessage({ type: 'IFRAME_READY' }, '*');

                if (this.accessToken && !AuthUtils.isTokenExpired(this.accessToken)) {
                    if (this._initialTokenResolve) {
                        this._initialTokenResolve();
                        this._initialTokenResolve = null;
                    }
                } else {
                    setTimeout(() => {
                        if (this._initialTokenResolve) {
                            this._initialTokenResolve();
                            this._initialTokenResolve = null;
                        }
                    }, 1500);
                }
            } else {
                if (this._initialTokenResolve) {
                    this._initialTokenResolve();
                    this._initialTokenResolve = null;
                }
            }
        }
    }

    waitForInitialToken(): Promise<void> {
        return this._initialTokenPromise ?? Promise.resolve();
    }

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        if (this._isEmbedded()) {
            sessionStorage.setItem('accessToken', token);
        } else {
            localStorage.setItem('accessToken', token);
        }
    }

    get accessToken(): string {
        if (this._isEmbedded()) {
            return sessionStorage.getItem('accessToken') ?? '';
        }
        return localStorage.getItem('accessToken') ?? '';
    }

    applyExternalToken(token: string | null | undefined): boolean {
        const normalizedToken = (token || '').trim();

        if (!normalizedToken) {
            return false;
        }

        if (!AuthUtils.isValidJwt(normalizedToken) || AuthUtils.isTokenExpired(normalizedToken)) {
            return false;
        }

        this.accessToken = normalizedToken;
        this.hydrateUserFromToken(normalizedToken);
        this._authenticated = true;
        this.ensureNavigationMenuLoaded();

        return true;
    }

    /**
     * Setter & getter for navigation menu
     */
    set navigationMenu(menu) {
        this._navigationMenu = menu;
    }

    get navigationMenu() {
        return this._navigationMenu;
    }

    set empresaData(empresa) {
        this._empresa = empresa;
    }

    get empresaData() {
        return this._empresa;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Métodos públicos
    // -----------------------------------------------------------------------------------------------------

    /**
     * Recuperar contraseña
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Restablecer contraseña
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Cambio contraseña
     *
     * @param payload
     */
    changePassword(payload: { password_actual: string, nueva_password: string, confirmacion_nueva_password: string }): Observable<any> {
        return this._httpClient.post(`${environment.seguridad.change_password}`, payload);
    }

    /**
     * Iniciar sesión
     *
     * @param credentials
     */
    signIn(credentials): Observable<any> {

        return this._httpClient.post(`${environment.login.login}`, credentials).pipe(
            switchMap((response: any) => {
                if (response.success) {
                    // Almacenar el token de acceso en el almacenamiento local
                    this.accessToken = response.token;
                    // Atencion sí cambia el estado tira 'undefined' y hay que volver a loguearse
                    // Seteamos el menú correspondiente al usuario logueado
                    this.navigationMenu = response.menu;
                    this._navigationService._navigationMenuData = this.navigationMenu;
                    this.empresaData = response.empresa;
                    this._empresaService._empresaData = response.empresa;

                    const usuario: User = {
                        id: response.usuario.codigo,
                        idempresa: this.empresaData?.idempresa,
                        name: `${response.usuario.nombre || ''} ${response.usuario.apellido || ''}`.trim() || response.usuario.nick,
                        avatar: response.usuario.foto_app,
                        email: response.usuario.email,
                        status: response.usuario.activo,
                        administrador_backlog: response.usuario.administrador_backlog
                    }
                    // Almacenar el usuario en el servicio de usuario
                    this._userService.user = usuario;

                    // Establecer el indicador de autenticación en verdadero
                    this._authenticated = true;

                    // Devolver un nuevo observable con la respuesta
                    return of(response);
                } else {
                    // Si no hay éxito, lanzar un error
                    return throwError("Inicio de sesión fallido");
                }
            })
        );
    }

    /**
     * Función que verifica las rutas que tiene asignadas el usuario logueado. Es utilizada por los guards.
     * @param path 
     * @returns boolean
     */
    verificarRutasPermitidas(path?): Observable<boolean> {
        const safePath = (path || '').toString();
        this.ensureNavigationMenuLoaded();

        if (this.rutas.length === 0 && this._navigationService._navigationMenuData?.length > 0) {
            this.extractRoutesFromMenu(this._navigationService._navigationMenuData);
        }

        if (this.rutas.length === 0) {
            return this.loadRoutes().pipe(
                switchMap(() => {
                    return of(this.hasRouteAccess(safePath));
                })
            );
        } else {
            return of(this.hasRouteAccess(safePath));
        }
    }

    /**
     * Cargar las rutas desde el menú de navegación
    */
    private loadRoutes(): Observable<void> {
        return new Observable<void>((observer) => {
            // Esperar a que se establezca el menú de navegación
            this.subscription = this._navigationService.navigation$.subscribe((navigation) => {
                if (navigation && navigation.default && navigation.default.length > 0) {
                    this.extractRoutesFromMenu(navigation.default);
                    observer.next();
                    observer.complete();
                    if (this.subscription) {
                        this.subscription.unsubscribe();
                    }
                }
            });
        });
    }

    /**
     * Función para extraer rutas del menú de navegación
     * @param menu
     */
    private extractRoutesFromMenu(menu): void {
        this.rutas = [];
        menu.forEach((item) => {
            this.tieneHijos(item.children);
        });
    }

    /**
     * Función recursiva que recorre los submenús.
     * @param hijos 
     */
    private tieneHijos(hijos): void {
        hijos.forEach(subItem => {
            if (subItem.link) {
                let ruta = subItem.link.replace('/', '');
                if (!this.rutas.includes(ruta)) {
                    this.rutas.push(ruta);
                }
            }
            if (subItem.children && subItem.children.length > 0) {
                this.tieneHijos(subItem.children);
            }
        });
    }

    /**
     * Función para verificar si los datos de la empresa ya han sido cargados
     */
    verificarDatosEmpresaCargados(): Observable<boolean> {
        // Comprobar si los datos de la empresa ya están cargados
        if (!this._empresa) {
            // Los datos de la empresa no están cargados, cárgalos
            return this.loadDatosEmpresa().pipe(
                switchMap(() => {
                    return of(true);
                })
            );
        } else if (!this.tiposArticuloPermitidos.length && !this.tipoCliente.length) {
            // Si los datos de la empresa están cargados pero los tipos de artículos no, cárgalos
            this.tiposArticuloPermitidos = this.parsearTiposArticulo(this._empresa);
            // Si los datos de la empresa están cargados pero el tipo de cliente no, cárgalos
            this.tipoCliente = this.parsearTipoCliente(this._empresa);
            return of(true);
        } else {
            // Los datos de la empresa, tipos de artículos y cliente asociado ya están cargados
            return of(true);
        }
    }


    /**
        * Cargar los datos de la empresa
    */
    private loadDatosEmpresa(): Observable<void> {
        return new Observable<void>((observer) => {
            // Esperar a que se establezcan los datos de la empresa
            this.subscription = this._empresaService.empresa$.subscribe((empresa) => {
                if (empresa) {
                    this.extractDatosEmpresa(empresa);
                    observer.next();
                    observer.complete();
                    if (this.subscription) {
                        this.subscription.unsubscribe();
                    }
                }
            });
        });
    }

    /**
     * Método para establecer los datos de la empresa
     */
    private extractDatosEmpresa(empresa): void {
        this._empresa = empresa;
        this.tiposArticuloPermitidos = this.parsearTiposArticulo(empresa);
        this.tipoCliente = this.parsearTipoCliente(empresa);
    }

    parsearTiposArticulo(data: IEmpresa): string[] {
        let tiposPermitidos: string[] = [];

        if (data.articulo_insumo === 1) {
            tiposPermitidos.push('insumo');
        }
        if (data.articulo_producto === 1) {
            tiposPermitidos.push('producto');
        }
        if (data.articulo_servicio === 1) {
            tiposPermitidos.push('servicio');
        }

        return tiposPermitidos;
    }

    parsearTipoCliente(data: IEmpresa): string[] {
        let tipoCliente: string[] = [];

        if (data.cliente_asociado === 1) {
            tipoCliente.push('cliente_asociado');
        }

        return tipoCliente;
    }


    signOut(): Observable<any> {
        if (this._isEmbedded()) {
            const prefKeys = Object.keys(sessionStorage).filter(k => k.startsWith('jedstion_pref'));
            const prefBackup: { [k: string]: string } = {};
            prefKeys.forEach(k => {
                const val = sessionStorage.getItem(k);
                if (val !== null) prefBackup[k] = val;
            });

            sessionStorage.clear();

            Object.keys(prefBackup).forEach(k => sessionStorage.setItem(k, prefBackup[k]));
        } else {
            // Preservar configuraciones visuales del operador (prefijo jedstion_pref)
            const prefKeys = Object.keys(localStorage).filter(k => k.startsWith('jedstion_pref'));
            const prefBackup: { [k: string]: string } = {};
            prefKeys.forEach(k => {
                const val = localStorage.getItem(k);
                if (val !== null) prefBackup[k] = val;
            });

            localStorage.clear();

            // Restaurar configuraciones visuales del operador
            Object.keys(prefBackup).forEach(k => localStorage.setItem(k, prefBackup[k]));
            sessionStorage.clear();
        }
        // Establecer el indicador de autenticación en falso
        this._authenticated = false;
        // Devolver el observable
        return of(true);
    }

    renewToken(): Observable<any> {
        return this._httpClient.post<any>(environment.menu, null);
    }

    /**
     * Registrarse
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Desbloquear sesión
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Verificar el estado de autenticación
     */
    check(): Observable<boolean> {
        // Comprobar si el usuario ha iniciado sesión
        if (this._authenticated) {
            return of(true);
        }

        // Comprobar la disponibilidad del token de acceso
        if (!this.accessToken) {
            return of(false);
        }

        // Comprobar la fecha de vencimiento del token de acceso
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // Si el token de acceso existe y no ha caducado, iniciar sesión con él
        return of(true);
    }

    getDataUser() {
        return this.user;
    }

    private hasRouteAccess(path: string): boolean {
        const cleanPath = (path || '').replace(/^\/+/, '');
        const primerSegmento = cleanPath.split('/')[0];
        if (!primerSegmento) {
            return false;
        }

        return this.rutas.some(r => {
            const cleanRuta = (r || '').replace(/^\/+/, '');
            const primerSegmentoRuta = cleanRuta.split('/')[0];
            return primerSegmentoRuta === primerSegmento;
        });
    }

    private ensureNavigationMenuLoaded(): void {
        if (!this._navigationService._navigationMenuData?.length) {
            void this._navigationService.getMenus();
        }
    }

    private hydrateUserFromToken(token: string): void {
        const payload = this.decodeTokenPayload(token);

        if (!payload) {
            return;
        }

        const userId = payload.codigo ?? payload.sub ?? payload.idusuario;
        if (!userId) {
            return;
        }

        const storedUserRaw = this._isEmbedded()
            ? sessionStorage.getItem('user')
            : localStorage.getItem('user');
        if (storedUserRaw) {
            try {
                const storedUser = JSON.parse(storedUserRaw);
                if (String(storedUser?.id) === String(userId)) {
                    return;
                }
            } catch {
                // Ignore parse errors and overwrite with a fresh user object.
            }
        }

        const user: any = {
            id: userId,
            idempresa: this.toNumber(payload.idempresa),
            name: payload.nick ?? payload.name ?? 'Usuario',
            email: payload.email ?? '',
            status: payload.activo ?? 1
        };

        this._userService.user = user;
    }

    private decodeTokenPayload(token: string): Record<string, any> | null {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const base64Payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - (base64Payload.length % 4)) % 4);
            const decodedPayload = atob(base64Payload + padding);

            return JSON.parse(decodedPayload);
        } catch {
            return null;
        }
    }

    private toNumber(value: unknown): number | undefined {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return undefined;
    }
}
